// DeploymentField — combobox + LLM-suggest control for one
// deployment-architecture intake field (2026-05-28 — Tier 1 + Tier 2
// for PEG-DEPLOYMENT-MANIFEST-001 follow-on).
//
// UX shape (per AskUserQuestion preview):
//
//   <Label>
//   ┌────────────────────────────────┐
//   │ AWS HIPAA-eligible          ▾ │   ← value shown in input
//   └────────────────────────────────┘
//   (when open)
//   ┌────────────────────────────────┐
//   │ ✓ AWS HIPAA-eligible           │   ← top suggestion (fit-tagged)
//   │   AWS GovCloud (FedRAMP)       │   ← also suggested
//   │   Azure HIPAA-eligible         │
//   │   GCP HIPAA (BAA)              │
//   │   ───────────                  │
//   │   + Other... → free text       │   ← escape hatch
//   └────────────────────────────────┘
//
// A small ✨ "Suggest for this project" button next to the field
// fires the Tier 2 endpoint. While in flight, the button shows a
// spinner; on success, the dropdown's top entries are replaced with
// LLM-ranked picks carrying project-specific rationale. On failure
// (LLM down, parse error, network), the static Tier 1 ordering
// stays visible and a small banner explains what fell back.

import { Loader2, Sparkles, ExternalLink } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError } from '@/lib/api'
import type {
  DeploymentCatalogEntry,
  RankedSuggestion,
  SuggestDeploymentBody,
} from '@/lib/api'

interface DeploymentFieldProps {
  /** Catalog field id (e.g. ``"cloud_provider"``). */
  fieldId: string
  /** Display label rendered above the input. */
  label: string
  /** Tooltip-style description shown under the label. */
  description?: string
  /** Curated catalog entries already re-ranked for the project context. */
  catalogEntries: DeploymentCatalogEntry[]
  /** Current field value (controlled). */
  value: string
  /** Called when the value changes (chip click OR free-text input). */
  onChange: (next: string) => void
  /** Free-form placeholder shown when the field is empty. */
  placeholder?: string
  /** Project context the Tier 2 endpoint needs to rank intelligently. */
  llmContext: {
    problem_statement: string
    shape: string | null
    regulatory: string[]
  }
}

export function DeploymentField({
  fieldId,
  label,
  description,
  catalogEntries,
  value,
  onChange,
  placeholder,
  llmContext,
}: DeploymentFieldProps) {
  const [open, setOpen] = useState(false)
  const [otherMode, setOtherMode] = useState(false)
  const [ranked, setRanked] = useState<RankedSuggestion[] | null>(null)
  const [rankingLoading, setRankingLoading] = useState(false)
  const [rankingError, setRankingError] = useState<string | null>(null)
  const [rankingTier, setRankingTier] = useState<'llm' | 'fallback' | null>(
    null,
  )
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Close the dropdown on outside-click.
  useEffect(() => {
    if (!open) return
    function handleClick(ev: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(ev.target as Node)) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', handleClick)
    return () => window.removeEventListener('mousedown', handleClick)
  }, [open])

  // Merge the LLM-ranked top-N (when present) with the catalog tail
  // so the dropdown shows curated options the LLM didn't pick too.
  // Rendering rule:
  //   1. LLM-ranked entries first (carry per-project rationale).
  //   2. Catalog entries the LLM didn't pick, in their existing order.
  const renderedOptions = useMemo(() => {
    if (!ranked || ranked.length === 0) {
      return catalogEntries.map((e) => ({
        value: e.value,
        label: e.label,
        rationale: e.rationale_template,
        doc_url: e.doc_url,
        isLlmRanked: false,
      }))
    }
    const seen = new Set(ranked.map((r) => r.value))
    const llmRows = ranked.map((r) => ({
      value: r.value,
      label: r.label,
      rationale: r.rationale,
      doc_url: r.doc_url,
      isLlmRanked: true,
    }))
    const catalogTail = catalogEntries
      .filter((e) => !seen.has(e.value))
      .map((e) => ({
        value: e.value,
        label: e.label,
        rationale: e.rationale_template,
        doc_url: e.doc_url,
        isLlmRanked: false,
      }))
    return [...llmRows, ...catalogTail]
  }, [ranked, catalogEntries])

  // Determine the catalog entry (if any) that matches the current
  // value verbatim — used to surface the doc_url next to the input.
  const matchingEntry = useMemo(
    () => renderedOptions.find((o) => o.value === value) ?? null,
    [renderedOptions, value],
  )

  const handleSuggest = useCallback(async () => {
    setRankingLoading(true)
    setRankingError(null)
    try {
      const body: SuggestDeploymentBody = {
        field_id: fieldId,
        problem_statement: llmContext.problem_statement,
        shape: llmContext.shape,
        regulatory: llmContext.regulatory,
        max_suggestions: 3,
      }
      const resp = await api.suggestDeployment(body)
      setRanked(resp.suggestions)
      setRankingTier(resp.tier)
      if (resp.warning) {
        setRankingError(resp.warning)
      }
      // Auto-open so the user sees the new top picks.
      setOpen(true)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : (err as Error).message
      setRankingError(`Suggestion failed: ${msg}`)
      setRanked(null)
      setRankingTier(null)
    } finally {
      setRankingLoading(false)
    }
  }, [fieldId, llmContext])

  // When the user toggles into "Other..." mode, the dropdown closes
  // and the input becomes free-text. Toggling back (clicking a chip)
  // resets the mode.
  const enterOtherMode = () => {
    setOtherMode(true)
    setOpen(false)
  }
  const pickValue = (v: string) => {
    onChange(v)
    setOtherMode(false)
    setOpen(false)
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center justify-between mb-1">
        <label
          className="block text-[10.5px] uppercase tracking-wider text-slate-500"
        >
          {label}
        </label>
        <button
          type="button"
          disabled={rankingLoading}
          onClick={handleSuggest}
          className="inline-flex items-center gap-1 text-[10px] text-indigo-300 hover:text-indigo-100 disabled:opacity-50"
          title="Ask the LLM to rank these options for THIS project"
        >
          {rankingLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {rankingLoading ? 'Thinking…' : 'Suggest for this project'}
        </button>
      </div>

      {description && (
        <p className="text-[10.5px] text-slate-500 mb-1">{description}</p>
      )}

      {/* Combobox input. In default mode it's read-only and clicking
          opens the dropdown. In ``otherMode`` it's a normal text input
          so the user can type a value the catalog doesn't have. */}
      <div className="relative">
        {otherMode ? (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder ?? 'Type a custom value'}
            className="w-full bg-slate-950/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500/60"
            autoFocus
            onBlur={() => {
              if (!value.trim()) setOtherMode(false)
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full bg-slate-950/60 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-indigo-500/60"
          >
            <span className={value ? '' : 'text-slate-600'}>
              {value || placeholder || `Choose ${label.toLowerCase()}`}
            </span>
            <span className="text-slate-500 text-xs">▾</span>
          </button>
        )}

        {/* Dropdown */}
        {open && !otherMode && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-slate-950/95 border border-slate-700 rounded-md shadow-xl max-h-80 overflow-y-auto">
            {renderedOptions.map((opt) => {
              const isPicked = opt.value === value
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => pickValue(opt.value)}
                  className={`w-full text-left px-3 py-2 text-sm border-b border-slate-800/60 last:border-b-0 hover:bg-indigo-500/10 ${
                    isPicked ? 'bg-indigo-500/15 text-indigo-100' : 'text-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {isPicked && (
                          <span className="text-indigo-300">✓</span>
                        )}
                        <span className="font-medium">{opt.label}</span>
                        {opt.isLlmRanked && (
                          <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-indigo-500/20 text-indigo-200">
                            AI ranked
                          </span>
                        )}
                      </div>
                      {opt.rationale && (
                        <div className="text-[10.5px] text-slate-400 mt-0.5">
                          {opt.rationale}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
            <button
              type="button"
              onClick={enterOtherMode}
              className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:bg-white/5 border-t border-slate-700/60"
            >
              + Other… (type a custom value)
            </button>
          </div>
        )}
      </div>

      {/* Sub-line: doc-url for the picked entry, plus any ranker warning. */}
      <div className="mt-1 flex items-center justify-between gap-2 min-h-[14px]">
        {matchingEntry?.doc_url ? (
          <a
            href={matchingEntry.doc_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-indigo-300"
            title="Open vendor documentation in a new tab"
          >
            <ExternalLink className="h-2.5 w-2.5" />
            vendor doc
          </a>
        ) : (
          <span />
        )}
        {rankingTier === 'fallback' && (
          <span className="text-[10px] text-amber-400/80" title={rankingError ?? ''}>
            ⚠ static fallback (LLM unavailable)
          </span>
        )}
        {rankingTier === 'llm' && !rankingError && (
          <span className="text-[10px] text-slate-500">✨ ranked for this project</span>
        )}
        {rankingError && rankingTier === null && (
          <span className="text-[10px] text-amber-400/80">{rankingError}</span>
        )}
      </div>
    </div>
  )
}
